import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  Users,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  Activity,
  Target,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
} from 'lucide-react';
import { BRAND_COLORS, CHART_COLOR_ARRAY } from '../constants/colors';
import type { CEODashboardData } from '../types/ceo-dashboard';

const API_URL = import.meta.env.VITE_API_URL || '';

// Expandable Section Component
interface ExpandableSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  badge?: string;
}

const ExpandableSection: React.FC<ExpandableSectionProps> = ({
  title,
  children,
  defaultExpanded = true,
  badge,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          {badge && (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
              {badge}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: number;
  trendLabel?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
  trendLabel,
}) => {
  const isPositive = trend !== undefined && trend > 0;
  const isNegative = trend !== undefined && trend < 0;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <div style={{ color }}>{icon}</div>
        </div>
        {trend !== undefined && (
          <div
            className={`flex items-center gap-1 text-sm font-semibold ${
              isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
            }`}
          >
            {isPositive && <TrendingUp className="w-4 h-4" />}
            {isNegative && <TrendingDown className="w-4 h-4" />}
            {Math.abs(trend).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
      <p className="text-3xl font-mono font-bold text-gray-900 mb-2">{value}</p>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      {trendLabel && <p className="text-xs text-gray-400 mt-1">{trendLabel}</p>}
    </div>
  );
};

// Alert Badge Component
const AlertBadge: React.FC<{ alert: { severity: string; message: string; action: string } }> = ({
  alert,
}) => {
  const severityColors = {
    critical: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const severityIcons = {
    critical: <XCircle className="w-5 h-5" />,
    warning: <AlertCircle className="w-5 h-5" />,
    info: <CheckCircle className="w-5 h-5" />,
  };

  return (
    <div
      className={`border rounded-lg p-4 ${severityColors[alert.severity as keyof typeof severityColors]}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          {severityIcons[alert.severity as keyof typeof severityIcons]}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm mb-1">{alert.message}</p>
          <p className="text-xs opacity-75">→ {alert.action}</p>
        </div>
      </div>
    </div>
  );
};

const CEODashboard: React.FC = () => {
  const [data, setData] = useState<CEODashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [granularity, setGranularity] = useState('day');
  const [expandedUseCases, setExpandedUseCases] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchData();
  }, [timeRange, granularity]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        time_range: timeRange,
        granularity: granularity,
      });
      const response = await fetch(`${API_URL}/admin/analytics/ceo-dashboard?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CEO dashboard data');
      }

      const dashboardData = await response.json();
      setData(dashboardData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Error fetching CEO dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: BRAND_COLORS.primary }}
        ></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading dashboard data</p>
        <p className="text-sm mt-1">{error || 'Unknown error'}</p>
        <button
          onClick={fetchData}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  const {
    business_health,
    profitability,
    strategic_insights,
    growth_trends,
    alerts,
    user_segmentation,
    operational_kpis,
  } = data;

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
          <span className="text-sm font-semibold text-gray-700">Time Range</span>
        </div>
        <div className="flex gap-2">
          {[
            { value: '24h', label: '24 Hours', gran: 'hour' },
            { value: '7d', label: '7 Days', gran: 'day' },
            { value: '30d', label: '30 Days', gran: 'day' },
            { value: '90d', label: '90 Days', gran: 'week' },
            { value: '1y', label: '1 Year', gran: 'month' },
          ].map((range) => (
            <button
              key={range.value}
              onClick={() => {
                setTimeRange(range.value);
                setGranularity(range.gran);
              }}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                timeRange === range.value
                  ? 'text-white shadow-sm'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
              style={timeRange === range.value ? { backgroundColor: BRAND_COLORS.primary } : {}}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts Section (if any) */}
      {alerts && alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map((alert, idx) => (
            <AlertBadge key={idx} alert={alert} />
          ))}
        </div>
      )}

      {/* Hero Metrics - Business Health Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={`$${business_health.mrr.toLocaleString()}`}
          subtitle={`${business_health.active_paying_users} paying customers`}
          icon={<DollarSign className="w-6 h-6" />}
          color={BRAND_COLORS.primary}
        />
        <MetricCard
          title="Gross Margin"
          value={`${profitability.gross_margin.toFixed(1)}%`}
          subtitle={`AI costs: $${profitability.llm_cost_30d.toFixed(2)}/mo`}
          icon={<TrendingUp className="w-6 h-6" />}
          color={
            profitability.gross_margin >= 70
              ? BRAND_COLORS.success
              : profitability.gross_margin >= 50
                ? BRAND_COLORS.warning
                : BRAND_COLORS.danger
          }
        />
        <MetricCard
          title="Active Users"
          value={business_health.active_users_30d.toString()}
          subtitle={`${user_segmentation.engagement_rate.toFixed(1)}% engagement rate`}
          icon={<Users className="w-6 h-6" />}
          color={BRAND_COLORS.purple}
          trend={business_health.user_growth_rate}
          trendLabel="vs last month"
        />
        <MetricCard
          title="Churn Rate"
          value={`${business_health.churn_rate.toFixed(1)}%`}
          subtitle={
            business_health.churn_rate < 5
              ? 'Healthy'
              : business_health.churn_rate < 10
                ? 'Monitor'
                : 'Action needed'
          }
          icon={<Activity className="w-6 h-6" />}
          color={
            business_health.churn_rate < 5
              ? BRAND_COLORS.success
              : business_health.churn_rate < 10
                ? BRAND_COLORS.warning
                : BRAND_COLORS.danger
          }
        />
        <MetricCard
          title="System Health"
          value={`${operational_kpis.success_rate.toFixed(1)}%`}
          subtitle={`${(operational_kpis.avg_response_time_ms / 1000).toFixed(1)}s avg response`}
          icon={<Shield className="w-6 h-6" />}
          color={operational_kpis.success_rate >= 95 ? BRAND_COLORS.success : BRAND_COLORS.warning}
        />
      </div>

      {/* Business Health & Growth */}
      <ExpandableSection title="📈 Business Health & Growth" defaultExpanded={true}>
        <div className="space-y-6 mt-4">
          {/* User Plan Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                User Distribution by Plan
              </h3>
              <div className="space-y-2">
                {business_health.users_by_plan.map((item, idx) => {
                  const total = business_health.total_users;
                  const percentage = ((item.count / total) * 100).toFixed(1);
                  return (
                    <div
                      key={item.plan}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: CHART_COLOR_ARRAY[idx % CHART_COLOR_ARRAY.length],
                          }}
                        />
                        <span className="font-medium text-gray-900 capitalize">{item.plan}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-gray-900">{item.count}</p>
                        <p className="text-xs text-gray-500">{percentage}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* User Segmentation */}
            <div>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700">User Segments</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Behavioral classification of your user base
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-green-600 font-medium">Power Users</p>
                    <div className="group relative">
                      <AlertCircle className="w-3 h-3 text-green-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Paying customers with high engagement (5+ simulations/month)
                      </div>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    {user_segmentation.power_users}
                  </p>
                  <p className="text-xs text-green-600 mt-1">High value, high engagement</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-yellow-600 font-medium">At Risk</p>
                    <div className="group relative">
                      <AlertCircle className="w-3 h-3 text-yellow-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Paying customers with low usage (0-1 simulations in last 30 days)
                      </div>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-yellow-700">
                    {user_segmentation.at_risk_users}
                  </p>
                  <p className="text-xs text-yellow-600 mt-1">Paying but not using</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-gray-600 font-medium">Inactive</p>
                    <div className="group relative">
                      <AlertCircle className="w-3 h-3 text-gray-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Users with no activity in the last 30 days
                      </div>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-gray-700">
                    {user_segmentation.inactive_users}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">No recent activity (30d)</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-blue-600 font-medium">Engagement Rate</p>
                    <div className="group relative">
                      <AlertCircle className="w-3 h-3 text-blue-500 cursor-help" />
                      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                        Percentage of users who were active in the last 30 days
                      </div>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    {user_segmentation.engagement_rate.toFixed(1)}%
                  </p>
                  <p className="text-xs text-blue-600 mt-1">Active users / Total users</p>
                </div>
              </div>
            </div>
          </div>

          {/* Growth Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                User Growth (Last 12 Months)
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={growth_trends.user_growth}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={BRAND_COLORS.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={BRAND_COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                  <YAxis tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="new_users"
                    stroke={BRAND_COLORS.primary}
                    fillOpacity={1}
                    fill="url(#colorUsers)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                MRR Trend (Last 12 Months)
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={growth_trends.revenue_trend}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                  <YAxis tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="mrr"
                    stroke={BRAND_COLORS.success}
                    strokeWidth={2}
                    dot={{ fill: BRAND_COLORS.success, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Profitability & Unit Economics */}
      <ExpandableSection title="💰 Profitability & Unit Economics" defaultExpanded={true}>
        <div className="space-y-6 mt-4">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Revenue per User</p>
              <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.primary }}>
                ${profitability.revenue_per_user.toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Cost per User</p>
              <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.warning }}>
                ${profitability.cost_per_active_user.toFixed(4)}
              </p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Cost per Simulation</p>
              <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.purple }}>
                ${profitability.cost_per_simulation.toFixed(4)}
              </p>
            </div>
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Cost/Revenue Ratio</p>
              <p
                className="text-2xl font-mono font-bold"
                style={{
                  color:
                    profitability.cost_to_revenue_ratio < 30
                      ? BRAND_COLORS.success
                      : profitability.cost_to_revenue_ratio < 50
                        ? BRAND_COLORS.warning
                        : BRAND_COLORS.danger,
                }}
              >
                {profitability.cost_to_revenue_ratio.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Cost Trend */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              AI Cost Trend (Last 30 Days)
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={growth_trends.cost_trend}>
                <defs>
                  <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={BRAND_COLORS.danger} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={BRAND_COLORS.danger} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                <YAxis tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number, name: string) =>
                    name === 'cost' ? `$${value.toFixed(2)}` : value
                  }
                />
                <Area
                  type="monotone"
                  dataKey="cost"
                  stroke={BRAND_COLORS.danger}
                  fillOpacity={1}
                  fill="url(#colorCost)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </ExpandableSection>

      {/* Strategic Insights */}
      <ExpandableSection title="🎯 Strategic Insights" defaultExpanded={true}>
        <div className="space-y-6 mt-4">
          {/* Top Customers */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Top 10 Customers by Value</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                      Email
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                      Plan
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Monthly Sims
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Est. LTV
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {strategic_insights.top_customers.map((customer, idx) => (
                    <tr key={customer.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm text-gray-900">{customer.email}</td>
                      <td className="py-2 px-3 text-sm">
                        <span
                          className="px-2 py-1 text-xs font-semibold rounded capitalize"
                          style={{
                            backgroundColor: `${CHART_COLOR_ARRAY[idx % CHART_COLOR_ARRAY.length]}20`,
                            color: CHART_COLOR_ARRAY[idx % CHART_COLOR_ARRAY.length],
                          }}
                        >
                          {customer.plan}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-sm text-gray-600 text-right font-mono">
                        {customer.monthly_simulations}
                      </td>
                      <td className="py-2 px-3 text-sm font-semibold text-gray-900 text-right font-mono">
                        ${customer.estimated_ltv}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Most Expensive Users */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Highest AI Cost Users (30 Days)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                      Email
                    </th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                      Plan
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      AI Cost
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Revenue
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Margin
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {strategic_insights.expensive_users.map((user) => (
                    <tr key={user.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm text-gray-900">{user.email}</td>
                      <td className="py-2 px-3 text-sm capitalize">{user.plan}</td>
                      <td
                        className="py-2 px-3 text-sm font-mono text-right"
                        style={{ color: BRAND_COLORS.danger }}
                      >
                        ${user.cost_30d.toFixed(2)}
                      </td>
                      <td
                        className="py-2 px-3 text-sm font-mono text-right"
                        style={{ color: BRAND_COLORS.success }}
                      >
                        ${user.monthly_revenue}
                      </td>
                      <td
                        className="py-2 px-3 text-sm font-semibold text-right"
                        style={{
                          color:
                            user.profit_margin > 50
                              ? BRAND_COLORS.success
                              : user.profit_margin > 0
                                ? BRAND_COLORS.warning
                                : BRAND_COLORS.danger,
                        }}
                      >
                        {user.profit_margin.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Use Case ROI - Enhanced Visuals */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-4">ROI by Use Case</h3>

            {/* Two Separate Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Cost Distribution Chart */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-medium text-gray-600 mb-3">
                  Cost Distribution by Use Case
                </h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={strategic_insights.use_case_roi} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="usecase" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                              <p className="text-sm font-semibold text-gray-900 mb-2">
                                {data.usecase}
                              </p>
                              <div className="space-y-1">
                                <p className="text-xs text-gray-600">
                                  Total Cost:{' '}
                                  <span className="font-mono font-bold text-red-600">
                                    ${data.cost.toFixed(2)}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Users:{' '}
                                  <span className="font-mono font-bold">{data.unique_users}</span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Cost/User:{' '}
                                  <span className="font-mono font-bold">
                                    ${data.cost_per_user.toFixed(4)}
                                  </span>
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="cost"
                      name="Cost ($)"
                      fill={BRAND_COLORS.danger}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Request Volume Chart */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-medium text-gray-600 mb-3">
                  Request Volume by Use Case
                </h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={strategic_insights.use_case_roi} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="usecase" width={120} tick={{ fontSize: 10 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                              <p className="text-sm font-semibold text-gray-900 mb-2">
                                {data.usecase}
                              </p>
                              <div className="space-y-1">
                                <p className="text-xs text-gray-600">
                                  Requests:{' '}
                                  <span
                                    className="font-mono font-bold"
                                    style={{ color: BRAND_COLORS.primary }}
                                  >
                                    {data.requests.toLocaleString()}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Users:{' '}
                                  <span className="font-mono font-bold">{data.unique_users}</span>
                                </p>
                                <p className="text-xs text-gray-600">
                                  Avg/User:{' '}
                                  <span className="font-mono font-bold">
                                    {(data.requests / data.unique_users).toFixed(1)}
                                  </span>
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="requests"
                      name="Requests"
                      fill={BRAND_COLORS.primary}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Use Case Summary Cards with Request Type Breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {strategic_insights.use_case_roi.map((usecase, idx) => {
                const isExpanded = expandedUseCases.has(usecase.usecase);
                const hasRequestTypes = usecase.request_types && usecase.request_types.length > 0;

                return (
                  <div
                    key={usecase.usecase}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2 flex-1">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: CHART_COLOR_ARRAY[idx % CHART_COLOR_ARRAY.length],
                            }}
                          />
                          <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                            {usecase.usecase}
                          </p>
                        </div>
                        {hasRequestTypes && (
                          <button
                            onClick={() => {
                              const newExpanded = new Set(expandedUseCases);
                              if (isExpanded) {
                                newExpanded.delete(usecase.usecase);
                              } else {
                                newExpanded.add(usecase.usecase);
                              }
                              setExpandedUseCases(newExpanded);
                            }}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Total Cost</span>
                          <span className="text-sm font-mono font-bold text-red-600">
                            ${usecase.cost.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Requests</span>
                          <span
                            className="text-sm font-mono font-bold"
                            style={{ color: BRAND_COLORS.primary }}
                          >
                            {usecase.requests}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Users</span>
                          <span className="text-sm font-mono font-bold text-gray-700">
                            {usecase.unique_users}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                          <span className="text-xs text-gray-600">Cost/User</span>
                          <span className="text-sm font-mono font-bold text-gray-700">
                            ${usecase.cost_per_user.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Request Type Breakdown - Expandable */}
                    {isExpanded && hasRequestTypes && (
                      <div className="border-t border-gray-200 bg-gray-50 p-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <Activity className="w-3 h-3" />
                          Request Type Breakdown
                        </h5>
                        <div className="space-y-3">
                          {usecase.request_types!.map((reqType, rtIdx) => (
                            <div
                              key={reqType.request_type}
                              className="bg-white rounded-md p-3 border border-gray-200"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    backgroundColor:
                                      CHART_COLOR_ARRAY[(idx + rtIdx) % CHART_COLOR_ARRAY.length],
                                  }}
                                />
                                <span className="text-xs font-medium text-gray-900">
                                  {reqType.request_type}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <span className="text-gray-500">Cost:</span>
                                  <span className="ml-1 font-mono font-bold text-red-600">
                                    ${reqType.cost.toFixed(4)}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Count:</span>
                                  <span
                                    className="ml-1 font-mono font-bold"
                                    style={{ color: BRAND_COLORS.primary }}
                                  >
                                    {reqType.count}
                                  </span>
                                </div>
                                <div className="col-span-2">
                                  <span className="text-gray-500">Avg/Req:</span>
                                  <span className="ml-1 font-mono font-bold text-gray-700">
                                    ${reqType.avg_cost_per_request.toFixed(4)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </ExpandableSection>

      {/* Operational Metrics */}
      <ExpandableSection title="⚙️ Operational Metrics" defaultExpanded={true}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4" style={{ color: BRAND_COLORS.success }} />
              <p className="text-xs text-gray-500">Success Rate</p>
            </div>
            <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.success }}>
              {operational_kpis.success_rate.toFixed(1)}%
            </p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" style={{ color: BRAND_COLORS.warning }} />
              <p className="text-xs text-gray-500">Avg Response Time</p>
            </div>
            <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.warning }}>
              {(operational_kpis.avg_response_time_ms / 1000).toFixed(1)}s
            </p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
              <p className="text-xs text-gray-500">Total Requests (30d)</p>
            </div>
            <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.primary }}>
              {operational_kpis.total_requests_30d.toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4" style={{ color: BRAND_COLORS.purple }} />
              <p className="text-xs text-gray-500">Simulations (30d)</p>
            </div>
            <p className="text-2xl font-mono font-bold" style={{ color: BRAND_COLORS.purple }}>
              {operational_kpis.simulations_30d}
            </p>
          </div>
        </div>
      </ExpandableSection>
    </div>
  );
};

export default CEODashboard;
